import ScoreArrow from '@/storybook/components/ScoreArrow/ScoreArrow';
import Text from '@/storybook/components/Text/Text';
import { formatNumber } from '@/utils';
import cx from 'classnames';
import { ReactNode, useMemo } from 'react';
import styles from './Score.module.css';

export const LABEL_COLORS = ['GREY', 'BLUE', 'BLUE_900'] as const;
export const SIZES = ['XS', 'S', 'SM', 'M', 'L'] as const;

export type ScoreProps = {
  'value': number;
  'valueSuffix'?: ReactNode;
  'valuePrefix'?: ReactNode;
  'valueColor'?: 'DARK' | 'LIGHT';
  'label'?: ReactNode;
  'labelColor'?: (typeof LABEL_COLORS)[number];
  'size'?: (typeof SIZES)[number];
  'isFormatValueDisabled'?: boolean;
  'displayZero'?: boolean;
  'arrowValue'?: number | null;
  'arrowSuffix'?: ReactNode;
  'isArrowDisabled'?: boolean;
  'isRoundedValue'?: boolean;
  'valueUnitTestId'?: string;
  'valueUnit'?: ReactNode;
  'valueUnitSuffix'?: ReactNode;
  'valueUnitPrefix'?: ReactNode;
  'valueUnitColor'?: 'DARK' | 'LIGHT';
  'valueUnitSize'?: 'XS' | 'S' | 'SM' | 'M' | 'L';
  'valueUnitIsFormatValueDisabled'?: boolean;
  'valueUnitDisplayZero'?: boolean;
  'valueUnitArrowValue'?: number;
  'data-testid'?: string;
  'label-testid'?: string;
  'value-testid'?: string;
  'arrow-testid'?: string;
  'dash-value-testid'?: string;
  'align'?: 'left' | 'center' | 'right';
  'className'?: string;
  'subtitle'?: ReactNode;
  'subtitle-testid'?: string;
  'reverseTitles'?: boolean;
};

export default function Score({
  value,
  valueSuffix,
  valuePrefix,
  valueColor = 'DARK',
  valueUnitSize,
  arrowValue,
  label,
  labelColor = 'GREY',
  size = 'M',
  isFormatValueDisabled = false,
  displayZero = false,
  isArrowDisabled,
  isRoundedValue = false,
  'data-testid': dataTestId = 'score',
  'label-testid': labelTestId = 'score-label',
  'value-testid': valueTestId = 'score-value',
  'arrow-testid': arrowTestId = 'score-arrow',
  'dash-value-testid': dashValueTestId = 'score-dash-value',
  align = 'center',
  arrowSuffix = '%',
  className,
  subtitle,
  reverseTitles,
  'subtitle-testid': subtitleTestId = 'score-subtitle',
}: ScoreProps) {
  const formattedValue = useMemo(() => {
    let newValue = !isRoundedValue ? value : Math.round(value);
    if (value === 0 && displayZero) return '0';
    if (value && !isFormatValueDisabled) return formatNumber(newValue, 1);
    if (value && isFormatValueDisabled) return newValue;
    if (!value) return '-';
  }, [isRoundedValue, value, displayZero, isFormatValueDisabled]);

  return (
    <div
      className={cx(styles.scoreContainer, className)}
      data-size={size}
      data-testid={dataTestId}
      data-align={align}
      data-is-reversed-titles={reverseTitles}
    >
      {label && (
        <Text as="div" className={styles.label} data-color={labelColor} data-testid={labelTestId}>
          {label}
        </Text>
      )}
      <section className={styles.valueAndArrowContainer}>
        <div
          className={styles.valueContainer}
          data-size={size}
          data-value-color={valueColor}
          data-testid={formattedValue === '-' ? dashValueTestId : valueTestId}
        >
          <span className={styles.prefix}>{valuePrefix}</span>
          <span className={styles.value}>
            {formattedValue}
            {valueSuffix && (
              <span className={styles.valueUnit} data-size={valueUnitSize || size}>
                {valueSuffix}
              </span>
            )}
          </span>
        </div>
        {!isArrowDisabled && (
          <ScoreArrow
            value={arrowValue}
            isDisplayParenthesis={size === 'XS'}
            data-testid={arrowTestId}
            arrowSuffix={arrowSuffix}
          />
        )}
      </section>
      {subtitle && (
        <Text as="div" className={styles.subtitle} data-testid={subtitleTestId}>
          {subtitle}
        </Text>
      )}
    </div>
  );
}
